import { useEffect, useState } from "react";
import { Link, useParams, useNavigate, NavLink, Routes, Route, Navigate } from "react-router-dom";
import api, { formatError } from "@/api/client";
import { toast } from "sonner";
import { ArrowLeft, FileText, Palette, Globe, Settings, BarChart3, Eye, Upload, Image as ImageIcon } from "lucide-react";

import ContentTab from "./space/ContentTab";
import AppearanceTab from "./space/AppearanceTab";
import DomainTab from "./space/DomainTab";
import SettingsTab from "./space/SettingsTab";
import AnalyticsTab from "./space/AnalyticsTab";
import MediaTab from "./space/MediaTab";

const TABS = [
  { to: "content", label: "Content", icon: FileText, testid: "tab-content" },
  { to: "appearance", label: "Appearance", icon: Palette, testid: "tab-appearance" },
  { to: "domain", label: "Domain", icon: Globe, testid: "tab-domain" },
  { to: "settings", label: "Settings", icon: Settings, testid: "tab-settings" },
  { to: "analytics", label: "Analytics", icon: BarChart3, testid: "tab-analytics" },
  { to: "media", label: "Media", icon: ImageIcon, testid: "tab-media" },
];

export default function SpaceEditorPage() {
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const [space, setSpace] = useState(null);
  const [loading, setLoading] = useState(true);

  const tabPath = (slug) => `/spaces/${spaceId}/${slug}`;

  const loadSpace = async () => {
    try {
      const { data } = await api.get(`/spaces/${spaceId}`);
      setSpace(data);
    } catch (e) {
      toast.error(formatError(e));
      navigate("/spaces");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSpace(); }, [spaceId]);

  const togglePublish = async () => {
    try {
      const ep = space.status === "published" ? "unpublish" : "publish";
      await api.put(`/spaces/${spaceId}/${ep}`);
      toast.success(space.status === "published" ? "Unpublished" : "Published");
      loadSpace();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  if (loading || !space) return <div className="p-8 text-zinc-500">Loading…</div>;

  return (
    <div className="flex flex-col min-h-screen">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-20">
        <div className="px-6 py-4 flex items-center gap-4">
          <Link to="/spaces" className="text-zinc-500 hover:text-zinc-900" data-testid="back-to-spaces">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-8 h-8 rounded-sm flex items-center justify-center text-white text-xs font-black"
              style={{ background: space.branding?.primary_color || "#41B5E6" }}>
              {space.name.charAt(0)}
            </div>
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wider text-zinc-500 font-bold">Space</div>
              <div className="font-bold truncate" data-testid="space-name-header">{space.name}</div>
            </div>
          </div>
          <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-sm ${
            space.status === "published" ? "bg-emerald-100 text-emerald-800" :
            space.status === "draft" ? "bg-zinc-100 text-zinc-700" : "bg-amber-100 text-amber-800"
          }`} data-testid="space-status-badge">{space.status}</span>
          {space.status === "published" && (
            <Link to={`/s/${space.slug}`} target="_blank"
              className="text-sm border border-zinc-300 hover:bg-zinc-50 px-3 py-1.5 rounded-sm flex items-center gap-1.5">
              <Eye className="w-3.5 h-3.5" /> Visit
            </Link>
          )}
          <button onClick={togglePublish} data-testid="toggle-publish-btn"
            className={`text-sm font-semibold px-4 py-1.5 rounded-sm ${
              space.status === "published"
                ? "border border-zinc-300 hover:bg-zinc-50"
                : "bg-[#41B5E6] hover:bg-[#2A9BCC] text-white"
            }`}>
            {space.status === "published" ? "Unpublish" : "Publish"}
          </button>
        </div>
        <div className="px-6 flex overflow-x-auto border-t border-zinc-100">
          {TABS.map((t) => (
            <NavLink key={t.to} to={tabPath(t.to)} end
              data-testid={t.testid}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  isActive ? "border-[#41B5E6] text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-900"
                }`
              }>
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
            </NavLink>
          ))}
        </div>
      </header>

      <div className="flex-1 bg-zinc-50">
        <Routes>
          <Route index element={<Navigate to="content" replace />} />
          <Route path="content" element={<ContentTab space={space} onReload={loadSpace} />} />
          <Route path="appearance" element={<AppearanceTab space={space} onReload={loadSpace} />} />
          <Route path="domain" element={<DomainTab space={space} onReload={loadSpace} />} />
          <Route path="settings" element={<SettingsTab space={space} onReload={loadSpace} />} />
          <Route path="analytics" element={<AnalyticsTab space={space} />} />
          <Route path="media" element={<MediaTab space={space} />} />
        </Routes>
      </div>
    </div>
  );
}
